import { useCallback, useEffect, useMemo, useReducer } from "react";
import { useLazyGetOrdersQuery } from "services/dfy-service";
import { useDebounce } from "./usedebounce";
import { DEFAULT_ROWS_PER_PAGE } from "../constants/orderConstants";

// Client-side sorting helper
const sortOrders = (orders, sortBy, sortOrder) => {
  if (!sortBy || !orders.length) return orders;

  const sorted = [...orders].sort((a, b) => {
    let valueA, valueB;

    switch (sortBy) {
      case "email":
        valueA = (a.user || "").toLowerCase();
        valueB = (b.user || "").toLowerCase();
        break;
      case "date":
        valueA = new Date(a.createdAt || 0).getTime();
        valueB = new Date(b.createdAt || 0).getTime();
        break;
      case "completeDate":
        valueA = a.lastCompletedDate ? new Date(a.lastCompletedDate).getTime() : 0;
        valueB = b.lastCompletedDate ? new Date(b.lastCompletedDate).getTime() : 0;
        break;
      case "status":
        valueA = (a.status || "").toLowerCase();
        valueB = (b.status || "").toLowerCase();
        break;
      case "paymentStatus":
        valueA = (a.paymentStatus || "").toLowerCase();
        valueB = (b.paymentStatus || "").toLowerCase();
        break;
      default:
        return 0;
    }

    if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
    if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
};

// Action types
const ACTIONS = {
  SET_ORDERS: "SET_ORDERS",
  SET_SEARCH: "SET_SEARCH",
  SET_STATUS: "SET_STATUS",
  SET_SORT: "SET_SORT",
  SET_PAGE: "SET_PAGE",
  SET_ROWS_PER_PAGE: "SET_ROWS_PER_PAGE",
  SET_PARTNER: "SET_PARTNER",
  SET_SUB_USER: "SET_SUB_USER",
  UPDATE_ORDER: "UPDATE_ORDER",
};

// Initial state
const initialState = {
  orders: [],
  totalCount: 0,
  searchText: "",
  status: "All",
  sortBy: "",
  sortOrder: "desc",
  page: 0,
  rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  partnerId: "",
  subUserId: "",
};

// Reducer function
const ordersReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_ORDERS:
      return {
        ...state,
        orders: action.payload.data,
        totalCount: action.payload.total,
      };

    case ACTIONS.SET_SEARCH:
      return { ...state, searchText: action.payload };

    case ACTIONS.SET_STATUS:
      return { ...state, status: action.payload, page: 0 };

    case ACTIONS.SET_SORT:
      // If clicking same column, toggle order; otherwise set new column with desc
      if (state.sortBy === action.payload) {
        return {
          ...state,
          sortOrder: state.sortOrder === "asc" ? "desc" : "asc",
        };
      }
      return { ...state, sortBy: action.payload, sortOrder: "desc" };

    case ACTIONS.SET_PAGE:
      return { ...state, page: action.payload };

    case ACTIONS.SET_ROWS_PER_PAGE:
      return { ...state, rowsPerPage: action.payload, page: 0 };

    case ACTIONS.SET_PARTNER:
      return { ...state, partnerId: action.payload, subUserId: "", page: 0 };

    case ACTIONS.SET_SUB_USER:
      return { ...state, subUserId: action.payload, page: 0 };

    case ACTIONS.UPDATE_ORDER:
      return {
        ...state,
        orders: state.orders.map((order) =>
          order._id === action.payload.orderId
            ? { ...order, ...action.payload.updates }
            : order
        ),
      };

    default:
      return state;
  }
};

/**
 * Custom hook for managing order list state and fetching
 */
export const useOrders = (options = {}) => {
  const { skipFetch = false } = options;
  const [state, dispatch] = useReducer(ordersReducer, initialState);

  const debouncedSearchText = useDebounce(state.searchText);

  // RTK Query hook for fetching orders
  const [
    fetchOrders,
    { data: queryData, isLoading, isFetching, error },
  ] = useLazyGetOrdersQuery();

  // Fetch orders from API (sorting is handled client-side)
  const fetchOrdersData = useCallback(async () => {
    if (skipFetch) return;

    const params = {
      page: state.page + 1,
      limit: state.rowsPerPage,
      query: debouncedSearchText,
      status: state.status,
    };

    // Add partner filter if selected
    if (state.partnerId) {
      params.partnerId = state.partnerId;
    }

    // Add sub-user filter if selected
    if (state.subUserId) {
      params.subUserId = state.subUserId;
    }

    fetchOrders(params);
  }, [
    skipFetch,
    state.page,
    state.rowsPerPage,
    debouncedSearchText,
    state.status,
    state.partnerId,
    state.subUserId,
    fetchOrders,
  ]);

  // Update local state when query data changes
  useEffect(() => {
    if (queryData) {
      dispatch({
        type: ACTIONS.SET_ORDERS,
        payload: {
          data: queryData.data ?? [],
          total: queryData.total,
        },
      });
    }
  }, [queryData]);

  // Fetch orders when dependencies change
  useEffect(() => {
    fetchOrdersData();
  }, [fetchOrdersData]);

  // Action handlers
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value.toLowerCase();
    dispatch({ type: ACTIONS.SET_SEARCH, payload: query });
  }, []);

  const handleStatusChange = useCallback((value) => {
    const status = value || "All";
    dispatch({ type: ACTIONS.SET_STATUS, payload: status });
  }, []);

  const handleSortChange = useCallback((columnId) => {
    dispatch({ type: ACTIONS.SET_SORT, payload: columnId });
  }, []);

  const handlePageChange = useCallback((_, newPage) => {
    dispatch({ type: ACTIONS.SET_PAGE, payload: newPage });
  }, []);

  const handleRowsPerPageChange = useCallback((e) => {
    dispatch({ type: ACTIONS.SET_ROWS_PER_PAGE, payload: e.target.value });
  }, []);

  const handlePartnerChange = useCallback((partnerId) => {
    dispatch({ type: ACTIONS.SET_PARTNER, payload: partnerId || "" });
  }, []);

  const handleSubUserChange = useCallback((subUserId) => {
    dispatch({ type: ACTIONS.SET_SUB_USER, payload: subUserId || "" });
  }, []);

  const updateOrder = useCallback((orderId, updates) => {
    dispatch({ type: ACTIONS.UPDATE_ORDER, payload: { orderId, updates } });
  }, []);

  const refetch = useCallback(() => {
    fetchOrdersData();
  }, [fetchOrdersData]);

  // Apply client-side sorting
  const sortedOrders = useMemo(() => {
    return sortOrders(state.orders, state.sortBy, state.sortOrder);
  }, [state.orders, state.sortBy, state.sortOrder]);

  return {
    ...state,
    orders: sortedOrders,
    isLoading: isLoading || isFetching,
    error,
    debouncedSearchText,
    handleSearchChange,
    handleStatusChange,
    handleSortChange,
    handlePageChange,
    handleRowsPerPageChange,
    handlePartnerChange,
    handleSubUserChange,
    updateOrder,
    refetch,
  };
};

export default useOrders;
